import {metadataFromFunctionCall} from '@github-ui/copilot-chat/components/FunctionLoadingUtils'
import {WithShimmerEffect} from '@github-ui/copilot-chat/components/WithShimmerEffect'
import type {SkillExecution} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {deriveToolGroupStatus, summarizeToolGroup} from '@github-ui/copilot-chat/utils/tool-grouping'
import {AlertIcon, CheckIcon, ChevronRightIcon, XIcon} from '@primer/octicons-react'
import {Spinner} from '@primer/react'
import {useCallback, useId, useMemo, useState} from 'react'

import {InlineToolCall} from '../InlineToolCall/InlineToolCall'
import styles from './ToolGroup.module.css'

export interface ToolGroupProps {
  skillExecutions: SkillExecution[]
}

export function ToolGroup({skillExecutions}: ToolGroupProps) {
  const status = useMemo(() => deriveToolGroupStatus(skillExecutions), [skillExecutions])
  const hasRunning = status === 'running'
  const [isOpen, setIsOpen] = useState(false)

  const summary = useMemo(
    () => summarizeToolGroup({skillExecutions, metadataFor: metadataFromFunctionCall}),
    [skillExecutions],
  )

  // Lead with a spinner while running, then switch to the shared skill icon once finished (members
  // all invoke the same skill, so the icon stays constant). Keep the icon monotonic: once any
  // member has finished (so a skill icon has been shown), don't regress to a spinner when a later
  // same-skill call starts and re-enters the running state — only spin while nothing has finished
  // yet. "Finished" is any non-running status (completed/unsupported/error). Error/partial keep a
  // distinct status icon once the whole group settles.
  const SkillIcon = useMemo(() => metadataFromFunctionCall(skillExecutions[0])?.icon, [skillExecutions])
  const hasFinishedMember = useMemo(
    () => skillExecutions.some(s => s.status !== 'started' && s.status !== 'progress'),
    [skillExecutions],
  )
  const showSpinner = hasRunning && !hasFinishedMember
  const iconStatus = status === 'error' || status === 'partial' ? status : undefined

  const panelId = useId()
  const titleId = useId()

  const handleClick = useCallback(() => setIsOpen(v => !v), [])

  if (skillExecutions.length <= 1) {
    const only = skillExecutions[0]
    if (!only) return null
    return <InlineToolCall skillExecution={only} />
  }

  return (
    <div className={styles.container} data-active={hasRunning} data-testid="tool-group">
      <button
        type="button"
        className={styles.header}
        onClick={handleClick}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span className={styles.statusIcon} data-status={iconStatus} aria-hidden="true">
          {showSpinner ? (
            <Spinner size="small" />
          ) : status === 'error' ? (
            <XIcon size={14} />
          ) : status === 'partial' ? (
            <AlertIcon size={14} />
          ) : SkillIcon ? (
            <SkillIcon />
          ) : (
            <CheckIcon size={14} />
          )}
        </span>
        <span className={styles.summary}>
          {hasRunning ? (
            <WithShimmerEffect>
              <span id={titleId} className={styles.summaryTitle}>
                {summary}
              </span>
            </WithShimmerEffect>
          ) : (
            <span id={titleId} className={styles.summaryTitle}>
              {summary}
            </span>
          )}
        </span>
        <ChevronRightIcon size={12} className={styles.chevron} data-expanded={isOpen} aria-hidden="true" />
      </button>
      {isOpen && (
        <div id={panelId} role="region" aria-labelledby={titleId} className={styles.members}>
          {skillExecutions.map((skill, index) => (
            <div key={skill.callId ?? `${skill.slug}-${index}`}>
              <InlineToolCall skillExecution={skill} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
